/**
 * CPU / memory / disk alert rules — one row each: on/off, warning and critical thresholds, how
 * many checks in a row (30 s apart) before it fires, and the quiet time after it fired. Edits are
 * kept in the form until Save, which saves all three.
 *
 * Used for a site's own rules (the site evaluates them; local page, and the cloud page through
 * the site's tunnel) and for the cloud's own rules (Cloud platform on the cloud page).
 */
import { useEffect, useState, type ReactNode } from 'react';
import { ServerStackIcon } from '@heroicons/react/24/outline';
import { Callout } from '../common/Callout';
import { AACard, AASwitch, aaInputCls } from './AlertActionsControls';
import { alertActionsErrorText, SYSTEM_RULE_LABEL, type SystemRule } from './alertActionsModel';
import { BTN_PRIMARY_SM } from '../common/ui';

export interface SystemRulesTableProps {
  title?: ReactNode;
  subtitle?: ReactNode;
  rules: SystemRule[] | null;
  /** Save every rule; resolves with what the server stored. */
  onSave: (rules: SystemRule[]) => Promise<SystemRule[]>;
  canEdit: boolean;
  /** Shown instead of the table, e.g. "the site is offline". */
  unavailable?: ReactNode;
  error?: string;
}

const NUM_FIELDS: { key: keyof SystemRule; label: string; unit: string; min: number; max: number }[] = [
  { key: 'warning_threshold', label: 'Warning at', unit: '%', min: 1, max: 100 },
  { key: 'critical_threshold', label: 'Critical at', unit: '%', min: 1, max: 100 },
  { key: 'consecutive_checks', label: 'Checks in a row', unit: '× 30 s', min: 1, max: 60 },
  // Still high after this long → one more iteration of the same alert (counted, not sent again).
  { key: 'cooldown_minutes', label: 'Count again every', unit: 'min', min: 1, max: 1440 },
];

export default function SystemRulesTable({
  title = 'CPU, memory & disk',
  subtitle = 'Checked every 30 seconds on every machine.',
  rules,
  onSave,
  canEdit,
  unavailable,
  error,
}: SystemRulesTableProps) {
  const [form, setForm] = useState<SystemRule[]>([]);
  const [saving, setSaving] = useState(false);
  const [result, setResult] = useState<{ tone: 'success' | 'danger'; text: string } | null>(null);

  useEffect(() => {
    if (rules) setForm(rules);
  }, [rules]);

  const dirty = !!rules && JSON.stringify(form) !== JSON.stringify(rules);
  const set = (i: number, patch: Partial<SystemRule>) => setForm((f) => f.map((r, j) => (j === i ? { ...r, ...patch } : r)));
  const problem = form.find((r) => Number(r.critical_threshold) < Number(r.warning_threshold));

  const save = async () => {
    setSaving(true);
    setResult(null);
    try {
      const stored = await onSave(form.map((r) => ({
        ...r,
        warning_threshold: Number(r.warning_threshold),
        critical_threshold: Number(r.critical_threshold),
        consecutive_checks: Number(r.consecutive_checks),
        cooldown_minutes: Number(r.cooldown_minutes),
      })));
      setForm(stored);
      setResult({ tone: 'success', text: 'Saved' });
    } catch (err) {
      setResult({ tone: 'danger', text: alertActionsErrorText(err, 'Could not save the rules') });
    } finally {
      setSaving(false);
    }
  };

  return (
    <AACard
      icon={ServerStackIcon}
      title={title}
      subtitle={subtitle}
      footer={unavailable || error ? undefined : (
        <div className="flex flex-wrap items-center gap-2">
          <span className={`min-w-0 flex-1 text-[12px] ${result?.tone === 'danger' || problem ? 'text-destructive' : 'text-muted-foreground'}`}>
            {problem
              ? `${SYSTEM_RULE_LABEL[problem.alert_type]}: the critical level cannot be below the warning level`
              : result?.text || (dirty ? 'Unsaved changes' : '')}
          </span>
          <button
            type="button"
            onClick={save}
            disabled={!canEdit || saving || !dirty || !!problem}
            className={BTN_PRIMARY_SM}
          >
            {saving ? 'Saving…' : 'Save'}
          </button>
        </div>
      )}
    >
      {unavailable ? (
        <p className="py-6 text-center text-[13px] text-muted-foreground">{unavailable}</p>
      ) : error ? (
        <Callout tone="danger">{error}</Callout>
      ) : !rules ? (
        <div className="space-y-2">{[0, 1, 2].map((i) => <div key={i} className="h-10 animate-pulse rounded-lg bg-muted" />)}</div>
      ) : (
        <div className="-mx-4 overflow-x-auto">
          <table className="w-full min-w-[40rem]">
            <thead>
              <tr className="border-b border-border">
                <th className="px-4 pb-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">Rule</th>
                {NUM_FIELDS.map((f) => (
                  <th key={f.key} className="px-2 pb-2 text-left text-[11px] font-semibold uppercase tracking-wider text-muted-foreground">{f.label}</th>
                ))}
              </tr>
            </thead>
            <tbody className="divide-y divide-border">
              {form.map((r, i) => (
                <tr key={r.alert_type} className={r.enabled ? '' : 'opacity-60'}>
                  <td className="px-4 py-2">
                    <span className="flex items-center gap-2.5">
                      <AASwitch
                        checked={r.enabled}
                        disabled={!canEdit}
                        label={`${SYSTEM_RULE_LABEL[r.alert_type]} alerts ${r.enabled ? 'on' : 'off'}`}
                        onChange={(v) => set(i, { enabled: v })}
                      />
                      <span className="text-[13px] font-medium text-foreground">{SYSTEM_RULE_LABEL[r.alert_type]}</span>
                    </span>
                  </td>
                  {NUM_FIELDS.map((f) => (
                    <td key={f.key} className="px-2 py-2">
                      <span className="flex items-center gap-1">
                        <input
                          type="number"
                          min={f.min}
                          max={f.max}
                          value={r[f.key] as number}
                          disabled={!canEdit || !r.enabled}
                          onChange={(e) => set(i, { [f.key]: e.target.value === '' ? '' : Number(e.target.value) } as Partial<SystemRule>)}
                          className={`${aaInputCls} mt-0 w-20`}
                        />
                        <span className="whitespace-nowrap text-[11px] text-muted-foreground">{f.unit}</span>
                      </span>
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
          <p className="mt-2 text-[12px] leading-snug text-muted-foreground">
            One alert per machine, from its first high check until it recovers. It fires after its checks in a row are at or above a level; while it stays high,
            every &ldquo;count again&rdquo; minutes it counts one more time on the same alert (nobody is told again — only if it turns critical). It recovers by
            itself once usage stays below the warning level for the same checks in a row; high again after that is a new alert.
          </p>
        </div>
      )}
    </AACard>
  );
}
